import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Clock, Plus, ArrowRight, Users } from 'lucide-react';
import { mockData } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { useTranslate } from '../hooks/useTranslate';

// Composant pour une carte individuelle
const CourseCard = ({ course, readingTimeTranslated, learnMoreTranslated }) => {
  const { translatedText: courseTitleTranslated } = useTranslate(course.title);
  const { translatedText: courseDescTranslated } = useTranslate(course.description);
  const { translatedText: courseDurationTranslated } = useTranslate(course.duration);
  
  return (
    <Card 
      className="group hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
      onClick={() => window.location.href = course.link}
    >
      <div className="relative overflow-hidden">
        <div className="bg-gray-100 flex items-center justify-center">
          <img 
            src={course.image} 
            alt={course.title}
            className="w-full h-auto object-contain"
            loading="lazy"
          />
        </div>
        
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
          <Plus className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-all duration-300" />
        </div>
      </div>
      
      <CardContent className="p-6">
        <div 
          className="flex items-center text-base text-gray-500 mb-3"
          style={{ fontFamily: '"Lora", serif' }}
        >
          {course.id === 4 ? (
            <Users className="w-4 h-4 mr-1" />
          ) : (
            <Clock className="w-4 h-4 mr-1" />
          )}
          {course.id === 4 ? (
            <span>{courseDurationTranslated}</span>
          ) : (
            <>{readingTimeTranslated}: {courseDurationTranslated}</>
          )}
        </div>
        
        <h3 
          className="text-xl font-semibold text-gray-900 mb-2 group-hover:transition-colors"
          style={{ 
            fontFamily: '"Lora", serif',
            color: 'rgb(196, 150, 96)'
          }}
        >
          {courseTitleTranslated}
        </h3>
        
        <p 
          className="text-gray-600 leading-relaxed mb-4"
          style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
        >
          {courseDescTranslated}
        </p>
        
        <Button 
          variant="ghost" 
          size="sm" 
          className="w-full justify-between hover:bg-opacity-20 transition-all"
          style={{ 
            fontFamily: '"Lora", serif',
            color: 'rgb(196, 150, 96)',
            backgroundColor: 'transparent'
          }}
          onClick={(e) => {
            e.stopPropagation();
            window.location.href = course.link;
          }}
        >
          <span>{learnMoreTranslated}</span>
          <ArrowRight className="w-4 h-4" />
        </Button>
      </CardContent>
    </Card>
  );
};

const LineagesHistory = () => {
  const { currentLanguage } = useLanguage();
  
  // Traductions des textes principaux
  const { translatedText: titleTranslated } = useTranslate("Histoire des Lignages");
  const { translatedText: descriptionTranslated } = useTranslate("Découvrez le riche patrimoine et les histoires fascinantes des Sept Nobles Maisons de Bruxelles");
  const { translatedText: readingTimeTranslated } = useTranslate("Temps de lecture");
  const { translatedText: learnMoreTranslated } = useTranslate("En savoir plus");
  
  // Traductions pour la section Bulletin
  const { translatedText: bulletinTitleTranslated } = useTranslate("Le Bulletin");
  const { translatedText: bulletinDescTranslated } = useTranslate("Le Bulletin publie des articles sur les lignages et le patrimoine bruxellois.");
  const { translatedText: readLatestTranslated } = useTranslate("Lire le dernier numéro");
  
  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 
            className="text-4xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: '"Lora", serif' }}
          >
            {titleTranslated}
          </h2>
          <p 
            className="text-gray-600 max-w-2xl mx-auto"
            style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
          >
            {descriptionTranslated}
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
          {mockData.courses.map((course) => (
            <CourseCard 
              key={course.id} 
              course={course}
              readingTimeTranslated={readingTimeTranslated}
              learnMoreTranslated={learnMoreTranslated}
            />
          ))}
        </div>
        
        {/* Publication highlight */}
        <div className="mt-12 text-center">
          <Card className="bg-white border-gray-200 max-w-md mx-auto">
            <CardContent className="p-6">
              <h3 
                className="text-2xl font-semibold mb-2"
                style={{ 
                  fontFamily: '"Lora", serif',
                  color: 'rgb(196, 150, 96)'
                }}
              >
                {bulletinTitleTranslated}
              </h3>
              <p 
                className="text-gray-700 mb-4 text-lg"
                style={{ fontFamily: '"Lora", serif' }}
              >
                {bulletinDescTranslated}
              </p>
              <Button 
                className="text-white hover:opacity-90"
                style={{ 
                  fontFamily: '"Lora", serif',
                  backgroundColor: 'rgb(196, 150, 96)'
                }}
                onClick={() => window.location.href = '/publications/bulletin'}
              >
                {readLatestTranslated}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default LineagesHistory;